import { Injectable } from '@angular/core';
import { Subject ,  Observable } from 'rxjs';
import { Router, NavigationStart } from '@angular/router';

import { Feedback, FeedbackType } from './feedback';

@Injectable({ providedIn: 'root' })
export class FeedbackService {

  private errorSubject = new Subject<Feedback>();
  private successSubject = new Subject<Feedback>();

  // Keep after navigation change will be used to know if we want to keep the feedback component (error or success)
  // After redirection
  private keepAfterNavigationChangeError = false;
  private keepAfterNavigationChangeSuccess = false;

  constructor(private router: Router) {
    // Clear feedback message on route change
    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterNavigationChangeError) {
          this.keepAfterNavigationChangeError = false;
        } else {
          // clear feedback
          this.errorSubject.next(null);
        }

        if (this.keepAfterNavigationChangeSuccess) {
          this.keepAfterNavigationChangeSuccess = false;
        } else {
          // clear feedback
          this.successSubject.next(null);
        }
      }
    });
  }

  /**
   * Remove old feedback
   */
  cleanState() {
    this.errorSubject.next(null);
    this.successSubject.next(null);
  }

  /**
   * Method call when error. Will notify error component.
   *
   * @param type      -- Type of error that we want to show. It could be dialog, snackBar, feedback.
   * @param message   -- Message to be displayed.
   * @param keepAfterNavigationChange
   */
  error(message: string, keepAfterNavigationChange = false, type = 'alert') {
    this.keepAfterNavigationChangeError = keepAfterNavigationChange;
    const feedback = new Feedback();
    feedback.type = FeedbackType.Error;
    feedback.message = message;
    this.errorSubject.next(feedback);
  }

  /**
   * Method call when success. Will notify success component.
   *
   * @param type      -- Type of success that we want to show. It could be dialog, snackBar, feedback.
   * @param message   -- Message to be displayed.
   * @param keepAfterNavigationChange
   */
  success(message: string, keepAfterNavigationChange = false, type = 'snackbar') {
    this.keepAfterNavigationChangeSuccess = keepAfterNavigationChange;
    const feedback = new Feedback();
    feedback.type = FeedbackType.Success;
    feedback.message = message;
    this.successSubject.next(feedback);
  }

  /**
   * Returns observable of feedback. Error component will subscribe to change on this.
   *
   * @returns
   */
  getError(): Observable<Feedback> {
    return this.errorSubject.asObservable();
  }

  /**
   * Returns observable of feedback. Success (feedback) component will subscribe to change on this.
   *
   * @returns
   */
  getSuccess(): Observable<Feedback> {
    return this.successSubject.asObservable();
  }
}
